import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { MessageCircle, Phone } from 'lucide-react';

const ContactPage = () => {
  const { t, language } = useLanguage();

  const whatsappNumber = '523331497166';
  const displayNumber = '+52 333 149 7166';

  const getMessage = () => {
    const messages = {
      es: 'Hola, me gustaría obtener más información sobre Jabad Guadalajara',
      en: 'Hello, I would like to get more information about Jabad Guadalajara',
      he: 'שלום, אשמח לקבל מידע נוסף על חב"ד גואדלחרה'
    };
    return encodeURIComponent(messages[language] || messages.es);
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] pt-20 pb-12">
      {/* Hero */}
      <section className="relative h-[300px] overflow-hidden mb-8">
        <div 
          className="absolute inset-0 bg-cover"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_jabad-gdl/artifacts/ruugius0_ChatGPT%20Image%20Apr%2016%2C%202026%2C%2001_03_33%20AM.png)',
            backgroundPosition: '50% 20%',
          }}>
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F2922]/70 to-[#0F2922]/50" />
        </div>
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center px-4">
            <h1 
              className="font-heading text-4xl sm:text-5xl font-bold text-white mb-2"
              data-testid="contact-title"
            >
              {t('contactTitle')}
            </h1>
            <p className="font-body text-white/80 text-lg max-w-md mx-auto">
              {t('contactMessage')}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* WhatsApp Card */}
          <a
            href={`https://wa.me/${whatsappNumber}?text=${getMessage()}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
            data-testid="whatsapp-contact"
          >
            <Card className="bg-white border-[#E5E3D8] rounded-2xl h-full hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <CardContent className="p-8 flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-2xl bg-[#25D366] flex items-center justify-center mb-6">
                  <MessageCircle className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-heading text-xl font-bold text-[#0F2922] mb-2">
                  {t('chatWhatsApp')}
                </h3>
                <p className="font-body text-[#5C6C62] mb-4">
                  {language === 'es' 
                    ? 'Respuesta rápida a todas tus preguntas' 
                    : language === 'en'
                    ? 'Quick response to all your questions'
                    : 'תגובה מהירה לכל השאלות שלך'}
                </p>
                <span className="font-body text-[#25D366] font-medium">
                  {displayNumber}
                </span>
              </CardContent>
            </Card>
          </a>

          {/* Phone Card */}
          <a
            href={`tel:${whatsappNumber}`}
            className="block"
            data-testid="phone-contact"
          >
            <Card className="bg-white border-[#E5E3D8] rounded-2xl h-full hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <CardContent className="p-8 flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-2xl bg-[#0F2922] flex items-center justify-center mb-6">
                  <Phone className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-heading text-xl font-bold text-[#0F2922] mb-2">
                  {t('callUs')}
                </h3>
                <p className="font-body text-[#5C6C62] mb-4">
                  {language === 'es' 
                    ? 'Llámanos directamente para asistencia inmediata' 
                    : language === 'en'
                    ? 'Call us directly for immediate assistance'
                    : 'התקשר אלינו ישירות לסיוע מיידי'}
                </p>
                <span className="font-body text-[#0F2922] font-medium">
                  {displayNumber}
                </span>
              </CardContent>
            </Card>
          </a>
        </div>

        {/* Large WhatsApp Button */}
        <div className="mt-12 text-center">
          <a
            href={`https://wa.me/${whatsappNumber}?text=${getMessage()}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-4 px-10 py-5 bg-[#25D366] text-white rounded-2xl font-heading text-xl font-bold hover:bg-[#25D366]/90 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
            data-testid="main-whatsapp-button"
          >
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            {t('chatWhatsApp')}
          </a>
        </div>

        {/* Info Section */}
        <div className="mt-12 p-8 bg-white border border-[#E5E3D8] rounded-2xl text-center">
          <h3 className="font-heading text-2xl font-bold text-[#0F2922] mb-4">
            Jabad Guadalajara
          </h3>
          <p className="font-body text-[#5C6C62] max-w-md mx-auto">
            {language === 'es' 
              ? 'Estamos aquí para servirte y ayudarte en todo lo que necesites. No dudes en contactarnos.'
              : language === 'en'
              ? 'We are here to serve you and help you with everything you need. Don\'t hesitate to contact us.'
              : 'אנחנו כאן לשרת אותך ולעזור לך בכל מה שאתה צריך. אל תהסס ליצור איתנו קשר.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
